import {
    Box,
    Heading,
    Text,
    VStack,
    Circle,
    Badge,
    Spinner,
    Flex,
} from "@chakra-ui/react";
import { CheckIcon } from "@chakra-ui/icons";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useEffect, useState } from "react";
import { getOrderTracking } from "./orderTracking";

/* =========================
   CONFIG
========================= */
const API_BASE =
    process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

export default function OrderTrackingPage() {
    const { orderId } = useParams<{ orderId: string; }>();
    const [order, setOrder] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    /* =========================
       LOAD ORDER
    ========================= */
    useEffect(() => {
        if (!orderId) return;
        let active = true;
        let inFlight = false;
        setLoading(true);
        setOrder(null);

        const loadOrder = async () => {
            if (inFlight) return;
            inFlight = true;
            try {
                const res = await axios.get(
                    `${API_BASE}/api/v1/orders/track/${orderId}`
                );
                if (active) setOrder(res.data.data);
            } catch (err) {
                console.error("Tracking error:", err);
            } finally {
                inFlight = false;
                if (active) setLoading(false);
            }
        };

        loadOrder();
        const interval = window.setInterval(loadOrder, 10000);
        return () => {
            active = false;
            window.clearInterval(interval);
        };
    }, [orderId]);

    if (loading) {
        return (
            <Box p={10} textAlign="center">
                <Spinner size="lg" />
            </Box>
        );
    }

    if (!order) {
        return (
            <Box p={10} textAlign="center">
                <Text>Order not found</Text>
            </Box>
        );
    }

    /* =========================
       STATUS NORMALIZATION
    ========================= */
    const tracking = getOrderTracking(order);

    const currentIndex = tracking.steps.findIndex(
        (step) => step.key === tracking.status
    );

    const safeIndex = currentIndex === -1 ? 0 : currentIndex;

    /* =========================
       UI
    ========================= */
    return (
        <Box mx="auto" p={{ base: 4, md: 8 }} bg={'#fff'}>
            <Heading color="teal.600" mb={2}>
                Track Your Order
            </Heading>

            <Text mb={6} color="gray.500">
                Order ID: {order._id}
            </Text>

            {/* =========================
               STATUS TIMELINE
            ========================= */}
            <Flex
                gap={8}
                wrap="wrap"
                align="center"
                justify="space-between"
                flexDirection={{ base: "column", md: "row" }}
            >
                {tracking.steps.map((step, index) => {
                    const isCompleted = step.key === "paid" ? tracking.isPaid && index < safeIndex : index < safeIndex;
                    const isActive = index === safeIndex;
                    const isPending = !isCompleted && !isActive;

                    return (
                        <VStack
                            key={step.key}
                            spacing={3}
                            opacity={isPending ? 0.35 : 1}
                            minW="120px"
                        >
                            {/* STATUS ICON */}
                            <Circle
                                size="48px"
                                bg={
                                    isCompleted
                                        ? "green.500"
                                        : isActive
                                            ? "teal.500"
                                            : "gray.300"
                                }
                                color="white"
                                border={
                                    isActive ? "3px solid #81E6D9" : "none"
                                }
                                animation={
                                    isActive
                                        ? "pulse 1.6s infinite, bounce 1.2s infinite"
                                        : undefined
                                }
                            >
                                {isCompleted ? (
                                    <CheckIcon />
                                ) : (
                                    <Text fontWeight="bold">
                                        {index + 1}
                                    </Text>
                                )}
                            </Circle>

                            {/* LABEL */}
                            <Text
                                fontSize="sm"
                                fontWeight={isActive ? "bold" : "normal"}
                                textAlign="center"
                            >
                                {step.label}
                            </Text>

                            {/* BADGES */}
                            {/* {isActive && (
                                <Badge colorScheme="teal">CURRENT</Badge>
                            )}
                            {isCompleted && (
                                <Badge colorScheme="green">DONE</Badge>
                            )} */}
                        </VStack>
                    );
                })}
            </Flex>

            {/* =========================
               ORDER DETAILS
            ========================= */}
            <Box
                mt={10}
                p={5}
                borderWidth="1px"
                borderRadius="md"
            >
                <Text><strong>Email:</strong> {order.email}</Text>
                <Text><strong>Total:</strong> MWK {order.total}</Text>
                {tracking.isDelivery && (
                    <Text><strong>Delivery Address:</strong> {order.address}</Text>
                )}

                <Badge mt={3} colorScheme="blue">
                    Status: {tracking.label}
                </Badge>
                <Badge mt={3} ml={2} colorScheme={tracking.isPaid ? "green" : "gray"}>
                    Payment: {tracking.isPaid ? "Paid" : tracking.paymentStatus.replace(/_/g, " ")}
                </Badge>
            </Box>

            {/* =========================
               ANIMATIONS
            ========================= */}
            <style>
                {`
                @keyframes pulse {
                    0% {
                        box-shadow: 0 0 0 0 rgba(56,178,172,0.6);
                    }
                    70% {
                        box-shadow: 0 0 0 16px rgba(56,178,172,0);
                    }
                    100% {
                        box-shadow: 0 0 0 0 rgba(56,178,172,0);
                    }
                }

                @keyframes bounce {
                    0%, 100% {
                        transform: translateY(0);
                    }
                    50% {
                        transform: translateY(-6px);
                    }
                }
                `}
            </style>
        </Box>
    );
}
